import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { 
  ArrowLeft, 
  Home, 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  PieChart,
  BarChart3,
  Users,
  CreditCard
} from "lucide-react";

interface RevenueData {
  total: number;
  thisMonth: number;
  lastMonth: number;
  thisYear: number;
  byTier: { [key: string]: number };
  byMonth: { month: string; amount: number }[];
  topPayers: Array<{
    name: string;
    email: string;
    amount: number;
    tier: string;
  }>;
  transactions: Array<{
    id: string;
    amount: number;
    tier: string;
    instructorEmail: string;
    paidAt: Date;
  }>;
}

export default function Revenue() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [timeRange, setTimeRange] = useState<"month" | "year" | "all">("month");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadRevenueData();
  }, [user, navigate]);

  const loadRevenueData = async () => {
    try {
      const [paymentsSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, "subscription_payments")),
        getDocs(collection(db, "users"))
      ]);

      const payments = paymentsSnap.docs
        .filter(doc => doc.data().status === 'approved')
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      const users = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));

      // Calculate totals - only admin-approved payments
      const total = payments.reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const thisYearStart = new Date(now.getFullYear(), 0, 1);

      const thisMonth = payments
        .filter((p: any) => {
          const paidAt = p.paidAt?.toDate?.() || new Date(0);
          return paidAt >= thisMonthStart;
        })
        .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

      const lastMonth = payments
        .filter((p: any) => {
          const paidAt = p.paidAt?.toDate?.() || new Date(0);
          return paidAt >= lastMonthStart && paidAt < thisMonthStart;
        })
        .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

      const thisYear = payments
        .filter((p: any) => {
          const paidAt = p.paidAt?.toDate?.() || new Date(0);
          return paidAt >= thisYearStart;
        })
        .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

      // Revenue by tier
      const byTier: { [key: string]: number } = {};
      payments.forEach((p: any) => {
        const tier = p.tier || "unknown";
        byTier[tier] = (byTier[tier] || 0) + (Number(p.amount) || 0);
      });

      // Revenue by month (last 12 months)
      const byMonth: { month: string; amount: number }[] = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 1);
        
        const amount = payments
          .filter((p: any) => {
            const paidAt = p.paidAt?.toDate?.() || new Date(0);
            return paidAt >= monthStart && paidAt < monthEnd;
          })
          .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

        byMonth.push({
          month: date.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' }),
          amount
        });
      }

      // Top payers
      const payerMap: { [key: string]: { amount: number; tier: string; name: string; email: string } } = {};
      payments.forEach((p: any) => {
        const instructorId = p.instructorId;
        if (!payerMap[instructorId]) {
          const instructor = users.find((u: any) => u.uid === instructorId);
          payerMap[instructorId] = {
            amount: 0,
            tier: p.tier || "unknown",
            name: instructor?.name || "ไม่ระบุ",
            email: instructor?.email || "ไม่ระบุ"
          };
        }
        payerMap[instructorId].amount += Number(p.amount) || 0;
      });

      const topPayers = Object.values(payerMap)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 10);

      // Recent transactions
      const transactions = payments
        .map((p: any) => {
          const instructor = users.find((u: any) => u.uid === p.instructorId);
          return {
            id: p.id,
            amount: Number(p.amount) || 0,
            tier: p.tier || "unknown",
            instructorEmail: instructor?.email || "ไม่ระบุ",
            paidAt: p.paidAt?.toDate?.() || new Date(0)
          };
        })
        .sort((a, b) => b.paidAt.getTime() - a.paidAt.getTime())
        .slice(0, 20);

      setRevenueData({
        total,
        thisMonth,
        lastMonth,
        thisYear,
        byTier,
        byMonth,
        topPayers,
        transactions
      });
    } catch (error) {
      console.error("Error loading revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    if (!revenueData) return;

    const csv = [
      ["Transaction ID", "Amount", "Tier", "Instructor", "Date"],
      ...revenueData.transactions.map(t => [
        t.id,
        t.amount.toString(),
        t.tier,
        t.instructorEmail,
        t.paidAt.toISOString()
      ])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-report-${new Date().toISOString()}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!revenueData) return null;

  const growthRate = revenueData.lastMonth > 0 
    ? ((revenueData.thisMonth - revenueData.lastMonth) / revenueData.lastMonth) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <DollarSign className="w-10 h-10 text-emerald-600" />
                  รายงานรายได้
                </h1>
                <p className="text-textLight">วิเคราะห์รายได้และการเงิน</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportData}
                className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200"
              >
                <Download className="w-5 h-5" />
                <span>ส่งออก</span>
              </button>
              <Link to="/">
                <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                  <Home className="w-5 h-5" />
                  <span>ไปหน้าแรก</span>
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">รายได้ทั้งหมด</p>
                <p className="text-2xl font-bold text-green-600">฿{revenueData.total.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">รายได้เดือนนี้</p>
                <p className="text-2xl font-bold text-blue-600">฿{revenueData.thisMonth.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                growthRate >= 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {growthRate >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-green-600" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-textLight">การเติบโต</p>
                <p className={`text-2xl font-bold ${
                  growthRate >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">รายได้ปีนี้</p>
                <p className="text-2xl font-bold text-blue-600">฿{revenueData.thisYear.toLocaleString()}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Revenue Chart */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                รายได้รายเดือน (12 เดือนล่าสุด)
              </h3>
              <div className="h-64 flex items-end justify-between gap-2">
                {revenueData.byMonth.map((data, index) => {
                  const maxAmount = Math.max(...revenueData.byMonth.map(m => m.amount));
                  const height = maxAmount > 0 ? (data.amount / maxAmount) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-xs font-semibold text-blue-600">
                        {data.amount > 0 ? `฿${(data.amount / 1000).toFixed(0)}k` : ""}
                      </div>
                      <div
                        className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all hover:opacity-80"
                        style={{ height: `${height}%`, minHeight: data.amount > 0 ? "20px" : "0" }}
                      ></div>
                      <div className="text-xs text-textLight rotate-45 origin-left">
                        {data.month}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Revenue by Tier */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <PieChart className="w-6 h-6 text-blue-600" />
                รายได้ตาม Tier
              </h3>
              <div className="space-y-4">
                {Object.entries(revenueData.byTier)
                  .sort(([, a], [, b]) => b - a)
                  .map(([tier, amount]) => {
                    const percentage = (amount / revenueData.total) * 100;
                    const colors: { [key: string]: string } = {
                      start: "bg-blue-500",
                      pro: "bg-blue-500",
                      yearly: "bg-yellow-600",
                      unknown: "bg-gray-400"
                    };
                    
                    return (
                      <div key={tier}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-textDark uppercase">{tier}</span>
                          <span className="text-sm text-textLight">
                            ฿{amount.toLocaleString()} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full ${colors[tier] || "bg-gray-400"}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </Card>
        </div>

        {/* Top Payers & Recent Transactions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Top Payers */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-green-600" />
                ผู้จ่ายสูงสุด
              </h3>
              <div className="space-y-3">
                {revenueData.topPayers.map((payer, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-bold text-textDark">{payer.name}</p>
                        <p className="text-sm text-textLight">{payer.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">฿{payer.amount.toLocaleString()}</p>
                      <p className="text-xs text-textLight uppercase">{payer.tier}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-blue-600" />
                ธุรกรรมล่าสุด
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {revenueData.transactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all">
                    <div>
                      <p className="text-sm font-semibold text-textDark">{transaction.instructorEmail}</p>
                      <p className="text-xs text-textLight">
                        {transaction.paidAt.toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">฿{transaction.amount.toLocaleString()}</p>
                      <p className="text-xs text-textLight uppercase">{transaction.tier}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
